import React, { useMemo } from "react";
import { Breadcrumbs, CopyableText, Pill, Table, VendorLogo } from "@truevault/aquilo";
import { TableDensity } from "@truevault/aquilo/src/components/table/Table";
import {
  ArrowPathIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { Routes } from "../../root/routes";
import { usePrimaryOrganization } from "../../hooks";
import { useServiceAccountSetup } from "../../hooks/useServiceAccountInbox";
import { ServiceAccountVendorDto } from "../../../common/service/server/dto/ServiceAccountSetupDto";
import { isServiceAccountsEnabled } from "../../util/ramsay";
import { ServiceAccountFlowDiagram } from "./ServiceAccountFlowDiagram";

const WHY_CARDS = [
  {
    icon: UserGroupIcon,
    title: "No shared human inbox",
    body: "One address covers every vendor login, so Ramsay never needs a teammate's email, password manager or phone to get past verification.",
  },
  {
    icon: ArrowPathIcon,
    title: "Hands-off logins",
    body: "2FA codes, magic links and password resets are read automatically. Ramsay can sign up, sign in and recover access without anyone watching an inbox.",
  },
  {
    icon: ShieldCheckIcon,
    title: "Scoped and auditable",
    body: "Each message is matched to the data recipient that sent it and kept in the Service Account Inbox with what was extracted from it and when Ramsay used it.",
  },
];

const SETUP_STEPS = [
  {
    title: "Give Ramsay the address",
    body: "When Ramsay creates or recovers a vendor account, it uses this organization's service account address as the login email.",
  },
  {
    title: "Let the vendor send email",
    body: "Anything a vendor sends to the address is delivered to Polaris and appears in the Service Account Inbox within seconds.",
  },
  {
    title: "Ramsay picks up the result",
    body: "The message is matched to the vendor that sent it and the code or link is extracted; Ramsay reads it over MCP and completes the login on its own.",
  },
];

// Inline rather than `md:grid-cols-3`: responsive grid utilities are not part of the prebuilt Tailwind CSS
const THREE_COLUMN_GRID: React.CSSProperties = {
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
};

/**
 * Explains what Ramsay's service accounts are, shows the organization's service account address, and lists
 * the data recipients mail to it is matched against (POL-7884, POL-7926).
 */
export const ServiceAccountSetupPage = () => {
  const [organization] = usePrimaryOrganization();
  const enabled = isServiceAccountsEnabled(organization);
  const { result: setup, request } = useServiceAccountSetup(
    organization?.id,
    !!organization && enabled,
  );

  const exampleEmail = setup?.email || `your-org@${setup?.emailDomain || "example.com"}`;

  const columns = useMemo(
    () => [
      {
        key: "vendorName",
        header: "Vendor",
        isVisuallyPrimary: true,
        render: (row: ServiceAccountVendorDto) => (
          <div className="flex items-center gap-3">
            <VendorLogo
              vendorLogoUrl={row.vendorLogoUrl ?? undefined}
              vendorName={row.vendorName}
              size="s"
            />
            <span>{row.vendorName}</span>
          </div>
        ),
      },
      {
        key: "dataRecipientId",
        header: "Data Recipient ID",
        render: (row: ServiceAccountVendorDto) => (
          <span className="text-gray-500">{row.dataRecipientId}</span>
        ),
      },
    ],
    [],
  );

  if (organization && !enabled) {
    return (
      <StandardPageWrapper className="tw-boundary">
        <Breadcrumbs
          title="Service Account Setup"
          source="Ramsay"
          sourceRoute={Routes.ramsay.root}
        />
        <p className="text-gray-600">
          The Service Accounts feature is not enabled for this organization.
        </p>
      </StandardPageWrapper>
    );
  }

  return (
    <StandardPageWrapper requests={[request]} className="tw-boundary">
      <Breadcrumbs title="Service Account Setup" source="Ramsay" sourceRoute={Routes.ramsay.root} />

      <section className="rounded-xl border border-gray-200 bg-white p-6 mb-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">How service accounts work</h2>
            <p className="text-gray-600">
              A service account is an email address Polaris owns on Ramsay's behalf. Ramsay uses it
              as the login for every vendor, and every email a vendor sends to it flows back through
              Polaris so Ramsay can act on it without a person in the loop.
            </p>
          </div>
          {setup && (
            <div className="shrink-0">
              {setup.domainConfigured ? (
                <Pill variant="completed">
                  Receiving on <span className="font-mono ml-1">@{setup.emailDomain}</span>
                </Pill>
              ) : (
                <Pill variant="warning">Email domain not configured</Pill>
              )}
            </div>
          )}
        </div>
        <div className="mt-6 max-w-5xl">
          <ServiceAccountFlowDiagram emailExample={exampleEmail} />
        </div>
        <p className="mt-3 text-sm text-gray-500">
          Every vendor uses the same address. Polaris works out which vendor a message is from using
          its sender, subject and body, matched against the data recipients below. A message it
          can't match to one is kept in the inbox but left uncategorized, so Ramsay never acts on
          it.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Why set them up</h2>
        <div className="grid gap-4" style={THREE_COLUMN_GRID}>
          {WHY_CARDS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-xl border border-gray-200 bg-white p-5">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-600 mb-3">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
              <p className="text-sm text-gray-600">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Setting up a vendor</h2>
        <ol className="grid gap-4" style={THREE_COLUMN_GRID}>
          {SETUP_STEPS.map((step, i) => (
            <li
              key={step.title}
              className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-5"
            >
              <span
                className="flex shrink-0 items-center justify-center rounded-full bg-primary-600 text-sm font-semibold text-white"
                style={{ width: 28, height: 28 }}
              >
                {i + 1}
              </span>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1" style={{ lineHeight: 1.35 }}>
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-1">
          <EnvelopeIcon className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Service account address</h2>
        </div>
        {setup && !setup.domainConfigured && (
          <p className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            No service account email domain is configured on this environment
            (RAMSAY_SERVICE_ACCOUNT_EMAIL_DOMAIN), so the address can't be generated yet and inbound
            mail is ignored.
          </p>
        )}
        {setup?.email && (
          <div className="mb-6 inline-block rounded-lg border border-gray-200 bg-white px-4 py-3">
            <CopyableText
              shortText={setup.email}
              longText={setup.email}
              tooltipText="Copy service account address"
              className="font-mono"
            />
          </div>
        )}
        <h3 className="font-semibold text-gray-900 mb-1">Vendors it receives mail for</h3>
        <p className="text-gray-600 mb-4">
          Every data recipient in this organization's data map. Add a vendor to the data map and
          mail from it can be matched here.
        </p>
        <Table
          data={setup?.vendors || []}
          columns={columns}
          tableDensity={TableDensity.COMPACT}
          keyExtractor={(row) => String(row.dataRecipientId)}
          zeroState={
            <p className="text-gray-500 py-6 text-center">
              This organization has no data recipients yet.
            </p>
          }
        />
      </section>
    </StandardPageWrapper>
  );
};
